'use client'

import { useMemo, useState } from "react";
import { Stack, styled, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { LocationOn, Mail, Phone, Place } from "@mui/icons-material";
import Image from "next/image";
import { Contact } from "@/app/lib/generated/graphql-types";
import LinkButton from "@/app/features/shared/__to_be_integrated/LinkButton";
import useIsDesktop from "@/hooks/useDesktop";
import parsePhoneNumber from 'libphonenumber-js'
import LinkOptional from "@/app/features/shared/__to_be_integrated/ui/LinkOptional";
import Link from "next/link";
import ErrorBox from "@/app/__to_be_integrated/_____components/_NEW/ErrorBox";

interface ContactViewProps {
    contact?: Contact,
    variant?: 'default' | 'address' | 'inline-address' | 'footer',
}

export default function ContactView({ contact, variant = 'default' }: ContactViewProps) {

    switch (variant) {
        case 'address':
            return <AddressVariant contact={contact} />;
        case 'inline-address':
            return <InlineAddressVariant contact={contact} />;
        case 'footer':
            return <FooterVariant contact={contact} />;
        default:
            return <DefaultVariant contact={contact} />;
    }
}

function DefaultVariant({ contact }: Omit<ContactViewProps, 'variant'>) {

    const telLinkAddress = useMemo(() => {
        if (!contact) return '';
        return parsePhoneNumber(contact!.phone.number, 'DE')?.getURI()
    }, [contact]);

    if (!contact) {
        return <ErrorBox error="Contact data is missing" />;
    }

    return (
        <Stack spacing={4} sx={{ width: '100%', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
            <Stack spacing={0} sx={{ mb: 14 }}>
                <Typography variant="h1" color="primary" sx={{ mb: 0 }}>{contact?.title}</Typography>
                <Typography variant="caption">
                    Träger: Stadt Erding, Oberbürgermeister Max Gotz, Landshuter Straße 1, 85435 Erding
                </Typography>
            </Stack>
            <Typography variant="h2">Kontakt</Typography>
            <Grid container columns={{ xs: 1, md: 3 }} columnSpacing={4} sx={{ width: '100%' }}>
                <Grid size={1} alignContent="center" textAlign="end">
                    <Typography variant="overline">Telefon</Typography>
                </Grid>
                <Grid size={2}>
                    <StyledTypography>
                        <StyledLinkOptional href={telLinkAddress}>{contact!.phone.number}</StyledLinkOptional>
                    </StyledTypography>
                </Grid>
                <Grid size={1}  alignContent="center" textAlign="end">
                    <Typography variant="overline" textAlign="end">Mail</Typography>
                </Grid>
                <Grid size={2}>
                    <StyledTypography>
                        <StyledLink href={`mailto:${contact!.emailAddress}`}>{contact!.emailAddress}</StyledLink>
                    </StyledTypography>
                </Grid>
                <Grid size={1}  alignContent="centers" textAlign="end">
                    <Typography variant="overline" textAlign="end">Adresse</Typography>
                </Grid>
                <Grid size={2}>
                    <StyledTypography fontWeight="bold">
                        {`${contact!.address.street}`}
                    </StyledTypography>
                    <StyledTypography fontWeight="bold">
                        {`${contact!.address.zipCode} ${contact!.address.city}`}
                    </StyledTypography>
                </Grid>
            </Grid>
        </Stack>
    )
}

// TODO: optimize hovering SSR friendly
function InlineAddressVariant({ contact }: Omit<ContactViewProps, 'variant'>) {
    const [isHovering, setIsHovering] = useState<boolean>(false);

    if (!contact) {
        return <ErrorBox size="inline" error="Contact data is missing" />;
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            <Typography
                variant="body2"
                sx={{
                    color: (theme) => isHovering ? theme.palette.secondary.main : theme.palette.secondary.dark,
                    fontSize: `calc(var(--current-font-size, var(--font-size-medium)))`,
                    textTransform: 'uppercase',
                    transition: 'color .3s ease, font-size .3s ease',
                    lineHeight: 1

                }}
            >
                 <span
                     style={{
                         transform: `scale(${isHovering ? 1 : .9})`,
                         transformOrigin: 'right center',
                         transition: 'transform .3s ease, color .3s ease',
                         display: 'inline-block',
                         willChange: 'transform',
                     }}
                 >
                    {`${contact.address.street}, ${contact.address.zipCode} ${contact.address.city}`}
                 </span>
            </Typography>
            <Place
                sx={{
                    color: (theme) => isHovering ? theme.palette.primary.main : theme.palette.secondary.dark,
                    fontSize: 'calc(var(--current-font-size, var(--font-size-medium)) * 1.25)',
                    scale: isHovering ? 1.25 : 1,
                    transition: 'color .3s ease, scale .3s ease',
                }}
            />
        </Stack>
    )
}

function AddressVariant({ contact }: Omit<ContactViewProps, 'variant'>) {
    if (!contact) {
        return <ErrorBox error="Contact data is missing" />;
    }

    return (
        <Stack spacing={2} alignItems="center" sx={{ width: '100%' }}>
            <Typography
                variant="h2"
                color="primary"
                textAlign='center'
            >
                Adresse
            </Typography>
            <Stack sx={{ width: '100%' }}>
                <Typography
                    variant="body2"
                    textAlign='center'
                >
                    {contact.address.street}
                </Typography>
                <Typography
                    variant="body2"
                    textAlign='center'
                >
                    {contact.address.zipCode}&nbsp;{contact.address.city}
                </Typography>
            </Stack>
            <LinkButton link={{ name: 'Weitere Details', url: '/contact' }}></LinkButton>
        </Stack>
    )
}

function FooterVariant({ contact }: Omit<ContactViewProps, 'variant'>) {


    const isDesktop = useIsDesktop();
    const telLinkAddress = useMemo(
        () => parsePhoneNumber(contact?.phone.number || '', 'DE')?.getURI(),
        [contact?.phone.number]
    );

    if (!contact) {
        return <ErrorBox error="Contact data is missing" />;
    }

    return (
        <Stack spacing={4}>
            <Image src="/assets/logo/logo_small_icon_and_text.webp" alt="Museum Erding logo" width={300} height={300}/>
            <Stack spacing={2}>
                {isDesktop
                    ? (
                        <Stack component={Link} href="/contact" aria-label="Kontakt" prefetch spacing={2} direction="row" sx={{ alignItems: 'center' }}>
                            <LocationOn />
                            <Typography variant="body2" fontWeight="bold">
                                {`${contact!.address.street}, ${contact!.address.zipCode} ${contact!.address.city}`}
                            </Typography>
                        </Stack>
                    )
                    : (
                        <Stack component={Link} href="/contact" aria-label="Kontakt" prefetch spacing={4} direction="row" sx={{ width: '100%', alignItems: 'center' }}>
                            <LocationOn />
                            <Stack spacing={0} direction="column" sx={{ alignItems: 'flex-start', flexGrow: 1}}>
                                <Typography variant="body2" fontWeight="bold">
                                    {contact!.address.street}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {`${contact!.address.zipCode} ${contact!.address.city}`}
                                </Typography>
                            </Stack>
                        </Stack>
                    )
                }
                <Stack spacing={{ xs: 4, md: 2 }} direction="row" sx={{ width: '100%', alignItems: 'center' }}>
                    <Mail />
                    <Typography variant="body2">
                        <StyledLink href={`mailto:${contact!.emailAddress}`}>{contact!.emailAddress}</StyledLink>
                    </Typography>
                </Stack>
                <Stack spacing={{ xs: 4, md: 2 }} direction="row" sx={{ width: '100%', alignItems: 'center' }}>
                    <Phone />
                    <Typography variant="body2">
                        <StyledLinkOptional href={telLinkAddress}>{contact!.phone.number}</StyledLinkOptional>
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    )
}

const StyledTypography = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
    fontSize: '1.5rem',
    textAlign: 'left',
}));

const StyledLink = styled('a', {
    shouldForwardProp: (prop) => prop !== 'primary'
})<{ primary?: boolean }>(({ theme, primary = false }) => ({
    color: primary ? theme.palette.primary.main : undefined,
    '&:hover': {
        color: theme.palette.primary.main,
    }
}));

const StyledLinkOptional = styled(LinkOptional, {
    shouldForwardProp: (prop) => prop !== 'primary'
})<{ primary?: boolean }>(({ theme, primary = false }) => ({
    color: primary ? theme.palette.primary.main : undefined,
    '&:hover': {
        color: theme.palette.primary.main,
    }
}));